import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLanguage } from '@/contexts/LanguageContext';
import { getOrganizations } from '@/lib/firebase/queries/organizations/getOrganizations';
import {
  deleteAssignmentRule,
  getAssignmentRules,
  RuleRefusedError,
  saveAssignmentRule,
} from '@/lib/firebase/queries/questionnaireRules';
import { specificity, type AssignmentRule } from '@/lib/assessment/questionnaireAssignment';
import type { Questionnaire } from '@/types/questionnaire';
import type { Organization } from '@/types/organization';

// The admin's questionnaire assignment rules (#92): who fills which
// questionnaire. The most specific matching rule wins; overlaps are refused.

const ALL = '__all__';
const K = 'dashboard.admin.questionnaires.rules';

const ageOrNull = (v: string) => (v.trim() === '' ? null : Math.max(0, Math.floor(Number(v))));

export function AssignmentRules({
  questionnaires,
  onRulesChange,
}: {
  questionnaires: Questionnaire[];
  onRulesChange?: (rules: AssignmentRule[]) => void;
}) {
  const { t, language } = useLanguage();
  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [org, setOrg] = useState(ALL);
  const [ageFrom, setAgeFrom] = useState('');
  const [ageTo, setAgeTo] = useState('');
  const [questionnaireId, setQuestionnaireId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const list = await getAssignmentRules();
    setRules(list);
    onRulesChange?.(list);
  };

  useEffect(() => {
    load();
    getOrganizations().then(setOrganizations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orgName = (id?: string | null) => {
    if (!id) return t(`${K}.allOrganizations`);
    const o = organizations.find((x) => x.id === id);
    return (language === 'ar' ? o?.nameAr || o?.name : o?.nameEn || o?.name) || id;
  };
  const questionnaireName = (id: string) => {
    const q = questionnaires.find((x) => x.id === id);
    return q ? (language === 'ar' ? q.name.ar : q.name.en) : id;
  };
  const ageText = (r: AssignmentRule) => {
    if (r.ageFrom == null && r.ageTo == null) return t(`${K}.allAges`);
    if (r.ageFrom == null) return t(`${K}.under`, { to: r.ageTo! });
    if (r.ageTo == null) return t(`${K}.fromAge`, { from: r.ageFrom });
    return t(`${K}.between`, { from: r.ageFrom, to: r.ageTo });
  };

  const add = async () => {
    if (!questionnaireId) {
      toast.error(t(`${K}.pickQuestionnaire`));
      return;
    }
    setBusy(true);
    try {
      await saveAssignmentRule({
        organizationId: org === ALL ? null : org,
        ageFrom: ageOrNull(ageFrom),
        ageTo: ageOrNull(ageTo),
        questionnaireId,
      });
      setOrg(ALL);
      setAgeFrom('');
      setAgeTo('');
      setQuestionnaireId('');
      await load();
      toast.success(t(`${K}.saved`));
    } catch (error) {
      toast.error(error instanceof RuleRefusedError ? t(`${K}.${error.problem}`) : t(`${K}.saveError`));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await deleteAssignmentRule(id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const sorted = [...rules].sort((a, b) => specificity(b) - specificity(a));
  const active = questionnaires.filter((q) => !q.archived);

  return (
    <Card data-testid="assignment-rules">
      <CardHeader>
        <CardTitle className="text-lg">{t(`${K}.title`)}</CardTitle>
        <p className="text-sm text-muted-foreground">{t(`${K}.description`)}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-start">{t(`${K}.organization`)}</TableHead>
              <TableHead className="text-start">{t(`${K}.age`)}</TableHead>
              <TableHead className="text-start">{t(`${K}.questionnaire`)}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{orgName(r.organizationId)}</TableCell>
                <TableCell>{ageText(r)}</TableCell>
                <TableCell className="font-medium">{questionnaireName(r.questionnaireId)}</TableCell>
                <TableCell className="text-end">
                  <Button variant="ghost" size="sm" onClick={() => remove(r.id)} disabled={busy} aria-label={t('common.delete')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  {t(`${K}.empty`)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className="grid gap-2 md:grid-cols-[1.5fr_0.7fr_0.7fr_1.5fr_auto] items-end">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{t(`${K}.organization`)}</label>
            <Select value={org} onValueChange={setOrg}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t(`${K}.allOrganizations`)}</SelectItem>
                {organizations.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {orgName(o.id)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{t(`${K}.ageFrom`)}</label>
            <Input type="number" min={0} value={ageFrom} onChange={(e) => setAgeFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{t(`${K}.ageTo`)}</label>
            <Input type="number" min={0} value={ageTo} onChange={(e) => setAgeTo(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{t(`${K}.questionnaire`)}</label>
            <Select value={questionnaireId} onValueChange={setQuestionnaireId}>
              <SelectTrigger>
                <SelectValue placeholder={t(`${K}.pickQuestionnaire`)} />
              </SelectTrigger>
              <SelectContent>
                {active.map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    {language === 'ar' ? q.name.ar : q.name.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={busy}>
            <Plus className="h-4 w-4" />
            {t(`${K}.add`)}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t(`${K}.howItWorks`)}</p>
      </CardContent>
    </Card>
  );
}
